<template>
  <div>
    <div class="filters-wrapper">
      <div class="form-group search-group">
        <input
          type="text"
          class="form-control full-width-input"
          placeholder="Search sessions, presenters, tracks, rooms…"
          aria-label="Search the agenda"
          v-model="search"
          @input="onHandleSearch"
        />

        <button
          type="button"
          class="search-clear"
          aria-label="Clear search"
          v-if="search"
          @click="onClearSearch"
        ><i class="fa-regular fa-xmark"></i></button>
      </div>

      <button
        class="btn btn-md btn-secondary"
        @click="onClearAgendaFilters"
        :disabled="isFetching"
      ><i class="fa-regular fa-filter-slash"></i> Clear All Filters</button>

      <button
        class="btn btn-md btn-primary"
        @click="onOpenAgendaFiltersModal"
        :disabled="isFetching"
      ><i class="fa-regular fa-filter"></i> Filter</button>
    </div>

    <div class="filters-summary" v-if="!isFetching">
      <div class="active-chips" v-if="activeChips.length">
        <button
          type="button"
          class="active-chip"
          v-for="chip in activeChips"
          :key="`chip-${chip.key}`"
          :aria-label="`Remove filter ${chip.label}: ${chip.value}`"
          @click="onRemoveChip(chip.key)"
        >
          <span class="active-chip__label">{{ chip.label }}:</span>
          <span class="active-chip__value">{{ chip.value }}</span>
          <i class="fa-regular fa-xmark"></i>
        </button>
      </div>

      <div class="results-count" v-if="totalCount">
        Showing {{ matchedCount }} of {{ totalCount }} sessions
      </div>
    </div>

    <div class="d-flex justify-content-center" v-if="isFetching">
      <event-spinner />
    </div>

    <div
      class="flex-table default-shadow"
      v-show="visibleDays.length && !isFetching"
    >
      <template v-for="day in visibleDays">
        <!-- group by agenda day -->
        <div class="agenda-day" :key="`day-${day.key}`">{{ day.day_short }}</div>

        <!-- sessions table grouped by day -->
        <table
          class="table table-bordered"
          v-for="sessionGroup in day.times"
          :key="`period-${day.key}-${sessionGroup.key}`"
        >
          <thead>
            <tr>
              <th class="table-header">
                <div class="time-wrapper">
                  <time-icon
                    :organization-id="organizationId"
                  />

                  <!-- session period -->
                  <div class="time">{{ sessionGroup.period }}</div>
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="row in sessionGroup.rows"
              :key="`session-${row.key}`"
            >
              <td>
                <div :class="{ 'sponsor-title-flex': row.session.sponsor }">
                  <div>
                    <div class="flex-table__session-title">
                      <!-- agenda name -->
                      <span
                        style="color: #000000"
                        v-if="row.session.is_agenda && row.session.name"
                        >{{ row.session.name }}</span
                      >

                      <!-- session name -->
                      <a
                        href="#"
                        v-if="!row.session.is_agenda && row.session.name"
                        @click.prevent="onOpenSessionDetailModal(row.session)"
                        :data-id="row.session.id"
                        >{{
                          (row.session.code && conferenceId != 8457)
                            ? `${row.session.code} - ${row.session.name}`
                            : row.session.name
                        }}</a
                      >
                    </div>

                    <!-- session description -->
                    <div
                      v-if="
                        (row.session.is_agenda && row.session.description) ||
                        !row.session.type
                      "
                    >
                      <div
                        v-if="!(conferenceId == 3357 && !row.session.type)"
                        style="padding: 5px 0; font-size: 14px"
                        v-html="row.description"
                      ></div>
                    </div>
                  </div>

                  <!-- session sponsor -->
                  <div v-if="row.session.sponsor && row.session.sponsor.photo_url">
                    <img
                      :src="row.session.sponsor.photo_url"
                      alt=""
                      style="padding: 5px; max-width: 300px"
                    />
                  </div>
                </div>

                <!-- presenters -->
                <div
                  class="flex-table__session-presenters"
                  v-if="row.session.presenters && row.session.presenters.length"
                >
                  <template
                    v-for="(presenter, presenterIndex) in row.session.presenters"
                  >
                    <a
                      href="#"
                      @click.prevent="onOpenPresenterDetailModal(presenter)"
                      :data-id="presenter.id"
                    >
                      {{ presenter.name }}
                    </a><span v-if="presenterIndex + 1 < row.session.presenters.length">;&nbsp;</span>
                  </template>
                </div>

                <!-- credit hours -->
                <div class="ce-credit-hours-wrapper">
                  <template v-if="+row.session.credit_hours > 0 && !removeCeInfo">
                    <span>
                      {{
                        conferenceId == 3300
                          ? "CE Contact Hours:"
                          : conferenceId == 2027
                          ? "CPD Credit Hours:"
                          : !row.session.type
                          ? "Hours:"
                          : "CE Credit Hours:"
                      }}
                    </span>
                    {{ row.session.credit_hours }}

                    <span class="black" v-if="row.session.is_virtual_session">
                      &nbsp;&nbsp;&nbsp; Live Interactive Webinar
                    </span>
                  </template>

                  <!-- session format -->
                  <div v-if="row.session.format">
                    <span>Format:</span>
                    <span class="font-weight-normal">{{ row.session.format }}</span>
                  </div>

                  <!-- session tracks -->
                  <div v-if="row.session.tracks && row.session.tracks.length">
                    <span>Track:</span>
                    <span
                      class="font-weight-normal"
                      v-for="(track, trackIndex) in row.session.tracks"
                      :key="`track-${trackIndex}`"
                      >{{ track
                      }}<span v-if="trackIndex + 1 < row.session.tracks.length"
                        >,&nbsp;
                      </span></span
                    >
                  </div>

                  <!-- isHybridConference -->
                  <div v-if="isHybridConference">
                    <span v-if="row.session.is_hybrid_session">
                      <i class="fa-regular fa-laptop-house"></i>&nbsp;&nbsp;
                      Hybrid Session
                    </span>

                    <span v-else>
                      <span v-if="row.session.is_inperson_session">
                        <i class="fa-regular fa-people-group"></i>&nbsp;&nbsp;
                        <span>In-Person Only</span>
                      </span>

                      <span v-if="row.session.is_virtual_session">
                        <i class="fa-regular fa-laptop"></i>&nbsp;&nbsp;
                        <span>Virtual Only</span>
                      </span>
                    </span>
                  </div>

                  <!-- event topics -->
                  <template v-if="row.session.topics && row.session.topics.length" v-for="topic in row.session.topics">
                    <div
                      class="event-topics"
                      v-if="topic.hours > 0"
                    >
                      <i class="fa-regular" :class="topic.icon"></i>&nbsp;
                      {{ topic.hours }}&nbsp;<span>{{ topic.title }}</span>
                    </div>
                  </template>
                </div>

                <!-- location -->
                <div class="flex-table__location" v-if="row.session.location">
                  {{ row.session.location }}
                </div>

                <!-- room name -->
                <div v-if="row.session.show_room_name">
                  <strong>Room: </strong> {{ row.session.room_name }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>

    <div class="show-more-wrapper" v-if="hasMore && !isFetching">
      <button
        type="button"
        class="btn btn-md btn-secondary"
        @click="onShowMore"
      >Show more ({{ remainingCount }} left)</button>
    </div>

    <div v-if="!visibleDays.length && !isFetching" class="flex-table default-shadow">
      <h4>Sessions not found</h4>
      <p v-if="hasAnyCriteria">Try a different search or clear the filters.</p>
    </div>

    <filters-modal
      :filters="agendaFilters"
      :search-filters.sync="searchFilters"
      @search="onApplyFilters"
      @clear="onClearAgendaFilters"
      :is-fetching="isFetching"
    />

    <presenter-detail-modal
      v-show="showPresenterDetailModal"
      :presenter="presenter"
    />

    <session-detail-modal
      v-if="showSessionDetailModal"
      :session="session"
      :conference-id="conferenceId"
      :slug="slug"
      :is-hybrid-conference="isHybridConference"
    />
  </div>
</template>

<script>
import { debounce } from "../../../global-declare-components/helpers/debounce";
import agendaAPI from "../../api/agendaAPI";
import TimeIcon from "./TimeIcon";
import PresenterDetailModal from "./PresenterDetailModal";
import SessionDetailModal from "./SessionDetailModal";
import FiltersModal from "./FiltersModal";

const DESCRIPTION_MAX_LENGTH = 500;
const SEARCH_DEBOUNCE = 300;
// Rendering, not filtering, is the cost on large agendas: ~60 rows costs ~100ms,
// 150 rows ~700ms. Agendas up to SMALL_AGENDA_LIMIT render in full (no chunking
// for the common case); bigger ones are revealed PAGE_SIZE rows at a time.
const SMALL_AGENDA_LIMIT = 200;
const PAGE_SIZE = 60;

// Filters that non-session agenda blocks carry no data for, so an active one has to
// hide them (as the server does in SessionService::transformSessionsTab()). `days`
// and `rooms` are deliberately absent: agenda blocks do have a day and a room, so
// those filters are evaluated against them instead of discarding them.
const SESSION_ONLY_FILTER_KEYS = ['types', 'visiting_types', 'formats', 'tracks'];

const CHIP_LABELS = {
  days: 'Day',
  types: 'Content Type',
  visiting_types: 'Visiting Type',
  formats: 'Session Format',
  rooms: 'Room',
  tracks: 'Track',
};

const EMPTY_FILTERS = () => ({
  days: '',
  formats: '',
  rooms: '',
  tracks: '',
  types: '',
  visiting_types: '',
});

// Combining marks left over from NFD, so "Kina" finds "Kina'u" and "Oiwi" finds "'Oiwi".
const DIACRITICS = /[\u0300-\u036f]/g;

// Whitespace, ASCII punctuation, typographic punctuation (curly quotes, dashes,
// ellipsis) and modifier letters such as the Hawaiian okina.
const WORD_SEPARATORS = /[\s!-\/:-@\[-`{-~\u00a0\u02b0-\u02ff\u2000-\u206f]+/g;

// " marriott ballroom 7 10 foyer " — padded so a leading space marks a word start
// and a trailing space marks a word end.
const toWordIndex = (value) => {
  const words = String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .replace(WORD_SEPARATORS, ' ')
    .trim();

  return words ? ` ${words} ` : '';
};

const stripTags = (value) => String(value || '').replace(/<[^>]*>/g, ' ');

// A <select> with no selected option makes Vue's v-model emit `undefined`, so an
// unset filter is not always the empty string.
const isFilterSet = (value) => value !== '' && value !== null && value !== undefined;

export default {
  props: {
    slug: {
      type: String,
      default: () => "",
    },
    organizationId: {
      type: Number,
      required: true,
    },
    conferenceId: {
      type: Number,
      required: true,
    },
    isHybridConference: {
      type: Boolean,
      required: true,
    },
    removeCeInfo: {
      type: String,
      default: () => "",
    },
  },
  components: {
    TimeIcon,
    FiltersModal,
    SessionDetailModal,
    PresenterDetailModal,
  },
  mounted() {
    this.loadAgenda();
  },
  data() {
    return {
      isFetching: false,
      // Flat, frozen search index — one entry per agenda row, built once per load.
      index: Object.freeze([]),
      showSessionDetailModal: false,
      session: {},
      showPresenterDetailModal: false,
      presenter: {},
      agendaFilters: {
        days: [],
        formats: [],
        rooms: [],
        tracks: [],
        types: [],
        visiting_types: []
      },
      search: '',
      // Debounced copy of `search` — the one the index is actually queried with.
      appliedSearch: '',
      // Draft state bound to the modal selects.
      searchFilters: EMPTY_FILTERS(),
      // Committed copy, applied only when the modal's Filter button is pressed.
      appliedFilters: EMPTY_FILTERS(),
      visibleCount: 0,
    }
  },
  computed: {
    totalCount() {
      return this.index.length;
    },

    pageSize() {
      return this.totalCount <= SMALL_AGENDA_LIMIT ? this.totalCount : PAGE_SIZE;
    },

    // Two rules keep the results honest:
    //   * a token matches on a word boundary, never mid-word, and a purely numeric
    //     token must match a whole word — "Marriott Ballroom 1" must not return
    //     Ballroom 10, nor a session that merely starts at 01:30 PM;
    //   * every token has to land in the SAME field (see matchedEntries), so the
    //     room name cannot supply "marriott ballroom" while the description
    //     supplies the "1".
    // Word order is still free, so "Health Sexual" finds "Sexual Health".
    queryTokens() {
      const words = toWordIndex(this.appliedSearch).trim();

      if (!words) {
        return [];
      }

      return words
        .split(' ')
        .map((token) => (/^[0-9]+$/.test(token) ? ` ${token} ` : ` ${token}`));
    },

    hasSessionOnlyFilter() {
      return SESSION_ONLY_FILTER_KEYS.some((key) => isFilterSet(this.appliedFilters[key]));
    },

    hasAnyCriteria() {
      return this.queryTokens.length > 0 || this.activeChips.length > 0;
    },

    matchedEntries() {
      const tokens = this.queryTokens;
      const filters = this.appliedFilters;
      const hideAgendaBlocks = this.hasSessionOnlyFilter;

      const dayId = isFilterSet(filters.days) ? +filters.days : null;
      const roomId = isFilterSet(filters.rooms) ? +filters.rooms : null;
      const trackId = isFilterSet(filters.tracks) ? +filters.tracks : null;
      const type = isFilterSet(filters.types) ? +filters.types : null;
      const visitingType = isFilterSet(filters.visiting_types) ? +filters.visiting_types : null;
      const format = isFilterSet(filters.formats) ? String(filters.formats).toLowerCase() : null;

      return this.index.filter((entry) => {
        if (hideAgendaBlocks && entry.isAgenda) return false;
        if (dayId !== null && entry.dayId !== dayId) return false;
        if (type !== null && entry.type !== type) return false;
        if (visitingType !== null && entry.visitingType !== visitingType) return false;
        if (format !== null && entry.format !== format) return false;
        if (roomId !== null && entry.roomId !== roomId) return false;
        if (trackId !== null && entry.trackIds.indexOf(trackId) === -1) return false;

        if (tokens.length) {
          // Cheap reject first: unless every token appears somewhere in the row, no
          // single field can hold them all.
          for (let i = 0; i < tokens.length; i++) {
            if (entry.words.indexOf(tokens[i]) === -1) return false;
          }

          if (tokens.length > 1 && !this.matchesSingleField(entry, tokens)) return false;
        }

        return true;
      });
    },

    matchedCount() {
      return this.matchedEntries.length;
    },

    remainingCount() {
      return Math.max(0, this.matchedCount - this.visibleCount);
    },

    hasMore() {
      return this.remainingCount > 0;
    },

    // Rebuilds the day -> period -> rows tree from the matched slice. The index is
    // built in document order, so a single pass is enough to regroup it.
    visibleDays() {
      const days = [];
      let currentDay = null;
      let currentPeriod = null;

      const rows = this.matchedEntries.slice(0, this.visibleCount);

      for (let i = 0; i < rows.length; i++) {
        const entry = rows[i];

        if (!currentDay || currentDay.key !== entry.dayKey) {
          currentDay = { key: entry.dayKey, day_short: entry.dayShort, times: [] };
          currentPeriod = null;
          days.push(currentDay);
        }

        if (!currentPeriod || currentPeriod.key !== entry.periodKey) {
          currentPeriod = { key: entry.periodKey, period: entry.period, rows: [] };
          currentDay.times.push(currentPeriod);
        }

        currentPeriod.rows.push(entry);
      }

      return days;
    },

    activeChips() {
      return Object.keys(CHIP_LABELS)
        .filter((key) => isFilterSet(this.appliedFilters[key]))
        .map((key) => {
          const options = this.agendaFilters[key] || [];
          const option = options.find(
            (item) => String(item.value) === String(this.appliedFilters[key])
          );

          return {
            key,
            label: CHIP_LABELS[key],
            value: option ? option.name : this.appliedFilters[key],
          };
        });
    },

    // Any change to the criteria must send the user back to the first chunk.
    criteriaSignature() {
      return [this.appliedSearch, ...Object.keys(CHIP_LABELS).map((key) => this.appliedFilters[key])].join('|');
    },
  },
  watch: {
    criteriaSignature() {
      this.visibleCount = this.pageSize;
    },
  },
  methods: {
    showModalsForScreenShots() {
      const urlParams = new URLSearchParams(window.location.search);
      const presenterId = urlParams.get('presenter_id');
      const sessionId = urlParams.get('session_id');

      if (!presenterId && !sessionId) {
        return;
      }

      if (sessionId) {
        const entry = this.index.find(
          (item) => !item.isAgenda && item.session.id === +sessionId
        );

        if (entry) {
          this.onOpenSessionDetailModal(entry.session);
        }
      }

      if (presenterId) {
        let presenter = null;

        for (let i = 0; i < this.index.length && !presenter; i++) {
          const presenters = this.index[i].session.presenters || [];
          presenter = presenters.find((item) => item.id === +presenterId) || null;
        }

        if (presenter) {
          this.onOpenPresenterDetailModal(presenter);
        }
      }
    },

    loadAgenda() {
      this.isFetching = true;

      agendaAPI
        .fetch(this.slug)
        .then((res) => {
          this.agendaFilters = res.data.filters || {};
          this.index = this.buildIndex(res.data.data || []);
          this.visibleCount = this.pageSize;

          this.showModalsForScreenShots();
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false;
        });
    },

    // One O(n) pass over the payload. Everything the search and the template need
    // per row is precomputed here so that re-filtering and re-rendering never do
    // string work again. The result is frozen so Vue skips making the whole
    // payload (sessions x presenters x materials x approvals) reactive.
    buildIndex(days) {
      const entries = [];

      for (let d = 0; d < days.length; d++) {
        const day = days[d];
        const times = day.times || [];

        for (let p = 0; p < times.length; p++) {
          const group = times[p];
          const items = group.items || [];

          for (let i = 0; i < items.length; i++) {
            const session = items[i];
            const fields = this.buildFields(session, group.period);

            entries.push({
              key: `${session.is_agenda ? 'a' : 's'}-${session.id}`,
              session,
              description: this.truncateDescription(session.description),
              fields,
              // Every field is space-padded, so concatenating them keeps word
              // boundaries intact and never fabricates a cross-field phrase.
              words: fields.join(''),
              dayKey: `${d}-${day.day_short}`,
              dayShort: day.day_short,
              periodKey: `${p}-${group.period}`,
              period: group.period,
              isAgenda: !!session.is_agenda,
              dayId: session.day_id === null || session.day_id === undefined ? null : +session.day_id,
              roomId: session.room_id === null || session.room_id === undefined ? null : +session.room_id,
              trackIds: (session.track_ids || []).map(Number),
              type: session.type === null || session.type === undefined ? null : +session.type,
              visitingType: session.visiting_type === null || session.visiting_type === undefined
                ? null
                : +session.visiting_type,
              format: session.format ? String(session.format).toLowerCase() : null,
            });
          }
        }
      }

      return Object.freeze(entries);
    },

    // One normalised string per searchable field. Kept separate (rather than one blob)
    // so a multi-word query has to be satisfied by a single field.
    buildFields(session, period) {
      const parts = [
        session.name,
        session.code,
        stripTags(session.description),
        session.location,
        session.room_name,
        session.format,
        period,
        session.sponsor ? session.sponsor.name : '',
      ];

      (session.presenters || []).forEach((item) => {
        parts.push(`${item.name || ''} ${item.title || ''}`);
      });
      (session.tracks || []).forEach((item) => parts.push(item));
      (session.topics || []).forEach((item) => parts.push(item.title));
      (session.learning_objectives || []).forEach((item) => parts.push(item));
      (session.learning_levels || []).forEach((item) => parts.push(item));

      const fields = [];

      for (let i = 0; i < parts.length; i++) {
        const field = toWordIndex(parts[i] || '');

        if (field) {
          fields.push(field);
        }
      }

      return fields;
    },

    matchesSingleField(entry, tokens) {
      const fields = entry.fields;

      for (let f = 0; f < fields.length; f++) {
        let all = true;

        for (let t = 0; t < tokens.length && all; t++) {
          if (fields[f].indexOf(tokens[t]) === -1) all = false;
        }

        if (all) return true;
      }

      return false;
    },

    truncateDescription(description) {
      const value = description || '';

      return value.length > DESCRIPTION_MAX_LENGTH
        ? value.slice(0, DESCRIPTION_MAX_LENGTH) + '...'
        : value;
    },

    onOpenSessionDetailModal(item) {
      setTimeout(() => {
        $("#sessionDetailModalVue").modal({
          show: true,
          backdrop: "static",
          keyboard: false,
        });
      }, 500);

      this.showSessionDetailModal = true;
      this.session = item || {};
    },

    onCloseSessionDetailModal() {
      this.showSessionDetailModal = false;
      this.session = null;
    },

    onOpenPresenterDetailModal(item) {
      setTimeout(() => {
        $("#presenterDetailModalVue").modal({
          show: true,
          backdrop: "static",
          keyboard: false,
        });
      }, 500);

      this.showPresenterDetailModal = true;
      this.presenter = item;
    },

    onClosePresenterDetailModal() {
      this.showPresenterDetailModal = false;
      this.presenter = null;
    },

    onHandleSearch: debounce(function () {
      this.appliedSearch = this.search;
    }, SEARCH_DEBOUNCE),

    onClearSearch() {
      this.search = '';
      this.appliedSearch = '';
    },

    onOpenAgendaFiltersModal() {
      // Discard any draft left over from a modal that was closed without applying.
      this.searchFilters = { ...this.appliedFilters };

      setTimeout(() => {
        $("#agendaFiltersModalVue").modal({
          show: true,
          backdrop: "static",
          keyboard: false,
        });
      }, 500);
    },

    onApplyFilters() {
      const applied = EMPTY_FILTERS();

      Object.keys(applied).forEach((key) => {
        if (isFilterSet(this.searchFilters[key])) {
          applied[key] = this.searchFilters[key];
        }
      });

      this.appliedFilters = applied;
    },

    onRemoveChip(key) {
      this.searchFilters[key] = '';
      this.onApplyFilters();
    },

    onShowMore() {
      this.visibleCount += PAGE_SIZE;
    },

    // "Clear All Filters" clears the text search too — it is one of the criteria the
    // results count reflects, so leaving it behind reads as a broken reset.
    onClearAgendaFilters() {
      this.searchFilters = EMPTY_FILTERS();
      this.appliedFilters = EMPTY_FILTERS();
      this.onClearSearch();
    }
  },
};
</script>

<style lang="scss" scoped>
.filters-wrapper {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  width: 100%;

  .form-group {
    flex-grow: 1;
    margin-bottom: 0;
  }

  .full-width-input {
    width: 100%;
    box-sizing: border-box;
    padding-right: 34px;
  }
}

.search-group {
  position: relative;
}

.search-clear {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  padding: 0 6px;
  border: 0;
  background: none;
  color: #6c757d;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: #212529;
  }
}

.filters-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 15px;
}

.active-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.active-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid #ced4da;
  border-radius: 20px;
  background: #f8f9fa;
  color: #212529;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #e9ecef;
  }

  &__label {
    font-weight: 600;
  }
}

.results-count {
  margin-left: auto;
  color: #6c757d;
  font-size: 14px;
}

.show-more-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 15px;
}
</style>
